/** 图生视频工作室：结果字段与任务列表展示约定 */

import {
  NAV_HUB_VIDEO_I2V_COVER,
  NAV_HUB_VIDEO_T2V_COVER,
} from '@/pages/ipe/workbench/constants/navHubCardImages';
import { MAX_UPLOAD_FILES_PER_SESSION } from '@/utils/uploadSessionLimits';

export const VIDEO_MAX_REFERENCES = MAX_UPLOAD_FILES_PER_SESSION;

export const VIDEO_CONCEPT_PRESETS = [
  { id: 'natural', label: '自然动态' },
  { id: 'casual', label: '休闲走动' },
  { id: 'magazine', label: '杂志写真' },
  { id: 'closeup', label: '特写展示' },
  { id: 'catwalk', label: '随心走秀' },
];

export const VIDEO_HERO_DEMO = {
  cover: NAV_HUB_VIDEO_I2V_COVER,
  caption: '上传商品或模特图，AI 生成符合平台调性的商品视频',
};

/** 文生视频主描述字数上限 */
export const VIDEO_T2V_PROMPT_MAX_LENGTH = 2500;

/** 图生视频文本补充字数上限 */
export const VIDEO_I2V_SUPPLEMENT_MAX_LENGTH = 2500;

/**
 * 视频画面比例（文生 / 图生共用）。
 * 前 6 项为常用电商/短视频比例（侧栏首屏展示），其余折叠。
 * 图生：HappyHorse 跟首帧比例，由后端裁首帧 + 成片兜底对齐。
 * 文生：原生支持多数比例；2:3 / 3:2 提交时映射最近原生比例后再裁成片。
 */
export const VIDEO_ASPECT_RATIO_OPTIONS = [
  { value: '16:9', label: '16:9', hint: '横屏' },
  { value: '9:16', label: '9:16', hint: '竖屏' },
  { value: '1:1', label: '1:1', hint: '方形' },
  { value: '3:4', label: '3:4', hint: '竖版' },
  { value: '4:3', label: '4:3', hint: '传统' },
  { value: '4:5', label: '4:5', hint: '社交' },
  { value: '5:4', label: '5:4', hint: '横版' },
  { value: '2:3', label: '2:3', hint: '海报竖' },
  { value: '3:2', label: '3:2', hint: '海报横' },
  { value: '21:9', label: '21:9', hint: '超宽' },
  { value: '9:21', label: '9:21', hint: '超竖' },
];

export const VIDEO_DEFAULT_ASPECT_RATIO = '9:16';

/** Temu 主图视频仅接受 1:1 / 3:4 / 16:9，其余比例会被平台拒收 */
export const VIDEO_TEMU_ASPECT_RATIO_VALUES = ['1:1', '3:4', '16:9'];

/** Temu 上架/自动规则场景可选比例（顺序沿用 VIDEO_ASPECT_RATIO_OPTIONS） */
export const VIDEO_TEMU_ASPECT_RATIO_OPTIONS = VIDEO_ASPECT_RATIO_OPTIONS.filter(
  (item) => VIDEO_TEMU_ASPECT_RATIO_VALUES.includes(item.value),
);

/** Temu 场景默认 3:4（9:16 不在允许列表内） */
export const VIDEO_TEMU_DEFAULT_ASPECT_RATIO = '3:4';

export const VIDEO_ASPECT_RATIO_VALUES = VIDEO_ASPECT_RATIO_OPTIONS.map((item) => item.value);

export function normalizeVideoAspectRatio(value) {
  const next = String(value || '').trim();
  return VIDEO_ASPECT_RATIO_VALUES.includes(next) ? next : VIDEO_DEFAULT_ASPECT_RATIO;
}

/** 比例预览框像素尺寸（适配侧栏网格，兼容超宽/超竖） */
export function getVideoAspectRatioFrameStyle(ratio, box = 34) {
  const [wRaw, hRaw] = String(ratio || '').split(':').map((n) => Number(n));
  const w = Number.isFinite(wRaw) && wRaw > 0 ? wRaw : 9;
  const h = Number.isFinite(hRaw) && hRaw > 0 ? hRaw : 16;
  const size = Math.max(20, Number(box) || 34);
  if (w >= h) {
    return {
      width: size,
      height: Math.max(8, Math.round((size * h) / w)),
    };
  }
  return {
    width: Math.max(8, Math.round((size * w) / h)),
    height: size,
  };
}

/** 视频时长（秒）：支持 3–15 连续选择 */
export const VIDEO_DURATION_MIN = 3;
export const VIDEO_DURATION_MAX = 15;
export const VIDEO_DEFAULT_DURATION_SEC = 5;
/** 侧栏快捷预设（仍可通过滑块选任意秒数） */
export const VIDEO_DURATION_PRESETS = [3, 5, 8, 10, 15];

/** 合法秒数保留；非法 / 越界回落默认 5 秒 */
export function normalizeVideoDurationSec(value) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n < VIDEO_DURATION_MIN || n > VIDEO_DURATION_MAX) {
    return VIDEO_DEFAULT_DURATION_SEC;
  }
  return n;
}

/** 画质设置（清晰度）：480P / 720P / 1080P */
export const VIDEO_GENERATION_MODE_OPTIONS = [
  { value: '480P', label: '480P', hint: '更快更省' },
  { value: '720P', label: '720P', hint: '均衡推荐' },
  { value: '1080P', label: '1080P', hint: '更清晰' },
];

export const VIDEO_DEFAULT_GENERATION_MODE = '720P';

export const VIDEO_GENERATION_MODE_VALUES = VIDEO_GENERATION_MODE_OPTIONS.map((item) => item.value);

/** 规范化画质设置；兼容旧值 standard → 720P、hd → 1080P */
export function normalizeVideoGenerationMode(value) {
  const raw = String(value || '').trim();
  if (!raw) return VIDEO_DEFAULT_GENERATION_MODE;
  const upper = raw.toUpperCase();
  if (VIDEO_GENERATION_MODE_VALUES.includes(upper)) return upper;
  if (raw === 'hd') return '1080P';
  if (raw === 'standard') return '720P';
  return VIDEO_DEFAULT_GENERATION_MODE;
}

export function getVideoGenerationModeLabel(value) {
  const mode = normalizeVideoGenerationMode(value);
  return VIDEO_GENERATION_MODE_OPTIONS.find((item) => item.value === mode)?.label || mode;
}

/** 随机数种子：写入模型 parameters.seed，取值 [0, 2147483647] */
export const VIDEO_SEED_MIN = 0;
export const VIDEO_SEED_MAX = 2147483647;
/** 与产品约定默认种子；缺省/非法时回落 */
export const VIDEO_DEFAULT_SEED = 13140;

export function normalizeVideoSeed(value) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n < VIDEO_SEED_MIN || n > VIDEO_SEED_MAX) {
    return VIDEO_DEFAULT_SEED;
  }
  return n;
}

/** 生成 [0, 2147483647] 内均匀随机整数 */
export function createRandomVideoSeed() {
  return Math.floor(Math.random() * (VIDEO_SEED_MAX + 1));
}

export const VIDEO_T2V_HERO_DEMO = {
  cover: NAV_HUB_VIDEO_T2V_COVER,
  caption: '输入文字描述，AI 生成符合平台调性的商品视频',
};

/**
 * 文生视频 · 模特提示词类型（拼入最终 prompt；none 表示不追加模特描述）。
 * 图生视频用上传模特图，不走本枚举。
 */
export const VIDEO_MODEL_PROMPT_OPTIONS = [
  { value: 'none', label: '不使用模特', hint: '纯商品/场景', prompt: '' },
  { value: 'female', label: '女模特', hint: '时尚女装', prompt: '画面中有一位时尚女模特自然展示商品，动作流畅、表情放松，电商广告质感' },
  { value: 'male', label: '男模特', hint: '男装展示', prompt: '画面中有一位时尚男模特自然展示商品，动作利落、镜头流畅，电商广告质感' },
  { value: 'kids', label: '童模', hint: '童装亲子', prompt: '画面中有一位可爱童模自然展示商品，动作活泼、氛围明亮，电商广告质感' },
  { value: 'lifestyle', label: '生活场景', hint: '日常出镜', prompt: '画面中有真人模特在生活场景中自然展示商品，动作真实、镜头平稳，种草短视频质感' },
];

export const VIDEO_DEFAULT_MODEL_PROMPT_TYPE = 'none';

export const VIDEO_MODEL_PROMPT_VALUES = VIDEO_MODEL_PROMPT_OPTIONS.map((item) => item.value);

export function normalizeVideoModelPromptType(value) {
  const next = String(value || '').trim();
  return VIDEO_MODEL_PROMPT_VALUES.includes(next) ? next : VIDEO_DEFAULT_MODEL_PROMPT_TYPE;
}

/** 文生：按类型取出可拼入 prompt 的模特描述；none 返回空串 */
export function resolveVideoModelPromptSnippet(type) {
  const key = normalizeVideoModelPromptType(type);
  return VIDEO_MODEL_PROMPT_OPTIONS.find((item) => item.value === key)?.prompt || '';
}

/** 图生：有模特图时追加的展示向描述（API 仅支持单首帧，靠文案引导出镜） */
export const VIDEO_I2V_MODEL_IMAGE_PROMPT =
  '画面中需要有真人模特自然展示商品，动作流畅、镜头平稳，电商主图视频质感';

export function isVideoStudioNav(navId) {
  return navId === 'videos' || navId === 'textToVideo';
}

export function isTextToVideoNav(navId) {
  return navId === 'textToVideo';
}
